import {
  Card,
  CardContent,
  Chip,
  ClickAwayListener,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEntity } from 'api/entity';
import React, { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { preprocessQuery, processQuery, rebuildQuery } from './processQuery';
import { SearchChildren } from './searchTypes/children';
import { SearchChildrenSearch } from './searchTypes/childrenSearch';
import { SearchCreateEntity } from './searchTypes/createEntity';
import { SearchGlobal } from './searchTypes/global';

export default function Search() {
  const [query, _setQuery] = useState('');
  const anchorRef = useRef<HTMLInputElement>(null);
  const [debouncedQuery] = useDebounce(query, 500);

  const [open, setOpen] = useState(false);
  const lastFocus = useRef<string>('');

  const searchQuery = processQuery(query);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasFocus, setHasFocus] = useState(false);

  const needsNewSearch = query != debouncedQuery;
  const isReadyToShow = !needsNewSearch && debouncedQuery.length;

  const setQuery: typeof _setQuery = (value) => {
    const newValue = typeof value === 'function' ? value(query) : value;
    _setQuery(preprocessQuery(newValue));
  };

  let adornment: React.ReactNode = null;

  const selectedEntity = useEntity(
    searchQuery.queryOptions.type == 'children'
      ? searchQuery.queryOptions.entityId
      : undefined,
  );

  useEffect(() => {
    if (!open) {
      anchorRef.current?.blur();
    }
  }, [open]);

  if (searchQuery.queryOptions.type === 'children') {
    adornment = (
      <Chip
        label={`Searching in: ${selectedEntity?.data?.name.slice(0, 10) ?? ''}`}
        size="small"
        sx={{ mr: 1 }}
      />
    );
  }

  if (searchQuery.queryOptions.type === 'currentEntity') {
    adornment = <Chip label="Current Entity" size="small" sx={{ mr: 1 }} />;
  }

  return (
    <>
      <ClickAwayListener
        onClickAway={() => lastFocus.current === 'input' && setOpen(false)}
      >
        <TextField
          placeholder="Search"
          value={
            searchQuery.queryOptions.type === 'childrenSearch'
              ? searchQuery.rawQuery
              : searchQuery.queryText
          }
          onChange={(e) =>
            setQuery(
              rebuildQuery({
                ...searchQuery,
                queryText: e.target.value,
              }),
            )
          }
          onKeyDown={(e) => {
            if (
              isReadyToShow &&
              e.key === 'Backspace' &&
              !searchQuery.queryText.length &&
              (searchQuery.queryOptions.type === 'children' ||
                searchQuery.queryOptions.type === 'currentEntity')
            ) {
              _setQuery('');
            }
          }}
          sx={{
            flex: '0 1 400px',
            '.MuiInputBase-root': {
              height: '100%',
            },
            input: {
              height: '100%',
              boxSizing: 'border-box',
            },
          }}
          inputRef={anchorRef}
          onFocus={() => {
            setHasFocus(true);
            lastFocus.current = 'input';
            setOpen(true);
          }}
          onBlur={() => {
            setHasFocus(false);
          }}
          InputProps={{
            autoComplete: 'off',
            startAdornment: adornment,
          }}
        />
      </ClickAwayListener>
      <ClickAwayListener
        onClickAway={() => lastFocus.current === 'popper' && setOpen(false)}
      >
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom"
          sx={{
            zIndex: 1200,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onMouseDown={() => {
            lastFocus.current = 'popper';
          }}
        >
          <Card
            variant="outlined"
            sx={{
              mt: 2,
              width: 'calc(min(100vw,1200px))',
              height: isReadyToShow ? '50vh' : undefined,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              direction="row"
              sx={{
                borderBottomWidth: 1,
                borderBottomColor: 'divider',
                borderBottomStyle: 'solid',
                p: 1,
              }}
            >
              {searchQuery.queryOptions.type === 'global' ||
              searchQuery.queryOptions.type === 'children' ||
              searchQuery.queryOptions.type === 'currentEntity' ? (
                <>
                  <Typography color="text.secondary">Results in</Typography>
                  &nbsp;
                  <Typography fontWeight="bold">
                    {searchQuery.queryOptions.type === 'global' && 'Global'}
                    {searchQuery.queryOptions.type === 'children' && 'Children'}
                    {searchQuery.queryOptions.type === 'currentEntity' &&
                      'Current Entity'}
                  </Typography>
                </>
              ) : null}
              {searchQuery.queryOptions.type === 'createEntity' && (
                <Typography fontWeight="bold">Create New Entity</Typography>
              )}
              {searchQuery.queryOptions.type === 'childrenSearch' && (
                <Typography color="text.secondary">Select an entity</Typography>
              )}
            </Stack>
            {!isReadyToShow ? (
              <Stack
                flexGrow={1}
                alignItems="center"
                justifyContent="center"
                gap={2}
                direction="row"
                py={8}
              >
                {needsNewSearch ? (
                  <Typography variant="h6">Stop typing to search</Typography>
                ) : !query ? (
                  <Typography variant="h6">Start typing to search</Typography>
                ) : (
                  <Typography variant="h6">No Results</Typography>
                )}
              </Stack>
            ) : (
              <CardContent sx={{ overflow: 'auto', px: 0 }}>
                {searchQuery.queryText[0] === '+' ? (
                  <SearchCreateEntity
                    value={searchQuery}
                    onClick={() => {
                      _setQuery('');
                      setOpen(false);
                    }}
                  />
                ) : (
                  <>
                    {searchQuery.queryOptions.type === 'global' && (
                      <SearchGlobal value={searchQuery} onClick={setOpen} />
                    )}
                    {(searchQuery.queryOptions.type === 'children' ||
                      searchQuery.queryOptions.type === 'currentEntity') && (
                      <SearchChildren value={searchQuery} onClick={setOpen} />
                    )}

                    {searchQuery.queryOptions.type === 'childrenSearch' && (
                      <SearchChildrenSearch
                        value={searchQuery}
                        onClick={setOpen}
                        setQuery={_setQuery}
                      />
                    )}
                  </>
                )}
              </CardContent>
            )}
          </Card>
        </Popper>
      </ClickAwayListener>
    </>
  );
}
